import { useEffect, useState } from "react";
import { RefreshCw, Lock } from "lucide-react";
import { useCloseSync } from "@/hooks/useEventStream";

/**
 * Fullscreen lockout while a Close sync is running.
 *
 * Reads from the SSE stream's `close_sync` event (pushed each time the
 * close_sync_state doc is mutated — `mark_started`, `mark_progress`,
 * `mark_done`, `record_sync`). Renders only when both `in_progress=true`
 * AND the running sync is a full pull. Incremental syncs (which fire
 * automatically at run-start) are sub-second and would just flash the
 * overlay, so they're excluded.
 *
 * Mounted in Layout so it covers every authenticated page.
 */
export function CloseSyncOverlay() {
  const status = useCloseSync();

  if (!status) return null;
  if (!status.in_progress || !status.running_force_full) return null;

  return <Overlay processed={status.running_processed} startedAt={status.running_started_at} />;
}

function Overlay({
  processed,
  startedAt,
}: {
  processed: number;
  startedAt: string | null;
}) {
  const [elapsed, setElapsed] = useState(elapsedSince(startedAt));
  useEffect(() => {
    const t = setInterval(() => setElapsed(elapsedSince(startedAt)), 1_000);
    return () => clearInterval(t);
  }, [startedAt]);

  // Throughput estimate; ~200 leads/page typical at Close API + rate-limit.
  const rate = elapsed > 0 ? processed / elapsed : 0;

  return (
    <div className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-6">
      <div className="card max-w-md w-full p-8 space-y-5 text-center shadow-2xl border border-accent/40">
        <div className="flex items-center justify-center">
          <div className="relative">
            <Lock className="h-10 w-10 text-accent/60" />
            <RefreshCw className="absolute -bottom-1 -right-1 h-5 w-5 text-accent animate-spin" />
          </div>
        </div>
        <div className="space-y-1">
          <h2 className="text-lg font-semibold">Full Close sync running</h2>
          <p className="text-sm text-muted">
            The UI is locked until the sync finishes. This usually takes a few
            minutes for ~15&nbsp;000 leads.
          </p>
        </div>
        <div className="grid grid-cols-3 gap-2 text-left">
          <Stat label="Pulled" value={processed.toLocaleString()} />
          <Stat label="Elapsed" value={formatDuration(elapsed)} />
          <Stat
            label="Rate"
            value={rate > 0 ? `${rate.toFixed(0)}/s` : "—"}
          />
        </div>
        <p className="text-[11px] text-muted">
          Triggered from this UI or via <code>cli.py sync-close --full</code>.
          Closing the tab is safe — the sync continues server-side.
        </p>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="card border-border p-2">
      <div className="text-[9px] uppercase tracking-wide text-muted">{label}</div>
      <div className="text-sm font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function elapsedSince(iso: string | null): number {
  if (!iso) return 0;
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s}s`;
}
